<script setup lang="ts">
import AppTable from "@/components/app-table.vue";
import type { FileDTO } from "@/models/dtos/file-dto";
import type { ServiceOrderDTO } from "@/models/dtos/service-order-dto";
import type { ReportsDTO } from "@/models/dtos/reports-dto";
import { ObjectField } from "@/models/object-field";
import { TableColumn } from "@/models/table-column";
import { notificationsService } from "@/services/notifications-service";
import { serviceOrdersService } from "@/services/service-orders-service";
import { formatDate } from "@/utils/date-utils";
import { blobToBase64 } from "@/utils/file-utils";
import { computed, onBeforeMount, ref, toRefs } from "vue";
import { RouteName } from "@/router";
import { useRouter } from "vue-router";
import { mdiFolderDownload, mdiUpload } from "@mdi/js";

const props = defineProps<{
  serviceOrderId: string | number;
}>();
const { serviceOrderId } = toRefs(props);

const pageLoading = ref(false);
const fileLoading = ref(false);
const serviceOrder = ref<ServiceOrderDTO | undefined>();

const files = ref<File[] | undefined>();
const base64ImageData = ref<string | undefined>();

const file = computed(() => files.value?.[0]);
const selectedForDownload = ref<number[]>();

const router = useRouter();

onBeforeMount(async () => {
  pageLoading.value = true;

  await loadServiceOrder();

  pageLoading.value = false;
});

const fields = ref([
  ObjectField.for("headline", "Rubrik", () => serviceOrder.value?.title),
  ObjectField.for(
    "facilityName",
    "Anläggning",
    () => serviceOrder.value?.facilityName,
  ),
  ObjectField.for("address", "Address", () => serviceOrder.value?.address),
  ObjectField.for(
    "postAddress",
    "Postadress",
    () => serviceOrder.value?.postAddress,
  ),
  ObjectField.for(
    "coordinates.x",
    "Kordinat X (RT90)",
    () => serviceOrder.value?.coordinates?.x,
  ),
  ObjectField.for(
    "coordinates.y",
    "Kordinat Y (RT90)",
    () => serviceOrder.value?.coordinates?.y,
  ),
  ObjectField.for("status", "Status", () => serviceOrder.value?.status),
  ObjectField.for(
    "technician",
    "Tekniker",
    () => serviceOrder.value?.technician,
  ),
  ObjectField.for(
    "orderNumber",
    "Beställarens Ärendenummer",
    () => serviceOrder.value?.orderNumber,
  ),
  ObjectField.for(
    "description",
    "Beskrivning",
    () => serviceOrder.value?.description,
  ),
  ObjectField.for(
    "customerReference",
    "Referens",
    () => serviceOrder.value?.customerReference,
  ),
  ObjectField.for(
    "contact.name",
    "Kontakt namn",
    () => serviceOrder.value?.contact?.name,
  ),
  ObjectField.for(
    "contact.phone",
    "Kontakt telefon",
    () => serviceOrder.value?.contact?.phone,
  ),
  ObjectField.for(
    "customer.name",
    "Slutkund namn",
    () => serviceOrder.value?.customer?.name,
  ),
  ObjectField.for(
    "customer.phone",
    "Slutkund telefon",
    () => serviceOrder.value?.customer?.phone,
  ),
]);

const historyColumns = ref([
  TableColumn.for("time", "Ändrad"),
  TableColumn.for("status", "Status"),
]);

const notesColumns = ref([
  TableColumn.for("time", "Rapporterad"),
  TableColumn.for("user", "Användare"),
  TableColumn.for("note", "Notering"),
]);

const reportsColumns = ref([
  TableColumn.for("name", "Namn"),
  TableColumn.for("time", "Rapporterad"),
  TableColumn.for("user", "Användare"),
]);

const filesColumns = ref([
  TableColumn.for("name", "Namn"),
  TableColumn.for("download", "Välj", { right: true, sort: false }),
]);

async function loadServiceOrder() {
  try {
    serviceOrder.value = await serviceOrdersService.getServiceOrder(
      serviceOrderId.value,
    );
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av serviceärendet.",
      type: "error",
      error,
    });
  }
}

async function onClickDownloadFile(file: FileDTO) {
  if (!serviceOrder.value) return;

  fileLoading.value = true;

  try {
    const blob = await serviceOrdersService.getServiceOrderFile(
      serviceOrder.value?.id,
      file.id,
    );

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = URL.createObjectURL(blob);
    a.download = file.name;
    a.click();
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid nedlanning av fil.",
      type: "error",
      error,
    });
  } finally {
    fileLoading.value = false;
  }
}

async function onUploadFile(event: Event) {
  if (event.target instanceof HTMLInputElement) {
    files.value = event?.target?.files ? Array.from(event?.target?.files) : [];
    const file = files.value[0];
    base64ImageData.value = file ? await blobToBase64(file) : undefined;
  }
}

function getId(item: FileDTO) {
  return item.id;
}

async function onSelectedForDownload(file: FileDTO) {
  if (!selectedForDownload.value) {
    selectedForDownload.value = [];
  }
  if (!selectedForDownload.value?.includes(file.id)) {
    selectedForDownload.value?.push(file.id);
  } else {
    const index = selectedForDownload.value?.indexOf(file.id);
    selectedForDownload.value?.splice(index, 1);
  }
}

async function onClickDownloadFiles() {
  if (!serviceOrder?.value?.files) return;

  fileLoading.value = true;

  const specificFilesForDownload = selectedForDownload?.value
    ? Array.from(selectedForDownload?.value)
    : [];

  let fileIds = serviceOrder?.value?.files.map(getId);
  if (specificFilesForDownload.length > 0) {
    fileIds = specificFilesForDownload;
  }

  const query: Record<string, unknown> = {
    fileId: fileIds,
  };

  try {
    const blob = await serviceOrdersService.getServiceOrderFiles(
      serviceOrder.value?.id,
      query,
    );

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = URL.createObjectURL(blob);
    a.download = serviceOrder.value.id + ".zip";
    a.click();
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid nedlanning av fil.",
      type: "error",
      error,
    });
  } finally {
    fileLoading.value = false;
  }
}

async function onSubmitUpload() {
  if (!file.value) {
    notificationsService.open({
      text: "Fil saknas.",
      type: "error",
    });

    return;
  }

  if (!serviceOrder.value?.id) return;

  try {
    fileLoading.value = true;
    const formData = new FormData();
    formData.append("file", file.value);

    await serviceOrdersService.addFile(serviceOrder.value.id, {
      file: file.value,
    });
    await loadServiceOrder();
    files.value = [];
    base64ImageData.value = undefined;
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid uppladdning av filen.",
      type: "error",
    });
  } finally {
    fileLoading.value = false;
  }
}

// async function onSubmitUpload() {
//   if (!file.value) {
//     notificationsService.open({
//       text: 'Fil saknas.',
//       type: 'error'
//     });
//
//     return;
//   }
//
//   if (!serviceOrder.value?.id) return;
//
//   try {
//     fileLoading.value = true;
//     console.log(file.value)
//     const formData = new FormData();
//     formData.append('file', file.value);
//
//     await serviceOrdersService.addFile(serviceOrder.value.id, formData);
//
//     await loadServiceOrder();
//     files.value = [];
//     base64ImageData.value = undefined;
//   } catch (error) {
//     notificationsService.open({
//       text: 'Något gick fel vid uppladdning av filen.',
//       type: 'error',
//       error
//     });
//   } finally {
//     fileLoading.value = false;
//   }
// }
//
// async function onSubmitUpload() {
//   if (!file.value) {
//     notificationsService.open({
//       text: 'Fil saknas.',
//       type: 'error'
//     });
//
//     return;
//   }
//
//   if (!serviceOrder.value?.id) return;
//
//   try {
//     fileLoading.value = true;
//     const formData = new FormData();
//     formData.append('file', file.value);
//
//     await serviceOrdersService.addFile(serviceOrder.value.id, file.value );
//     await loadServiceOrder();
//     files.value = [];
//     base64ImageData.value = undefined;
//   } catch (error) {
//     notificationsService.open({
//       text: 'Något gick fel vid uppladdning av filen.',
//       type: 'error',
//       error
//     });
//   } finally {
//     fileLoading.value = false;
//   }
// }

function onClickReport(report: ReportsDTO) {
  router.push({
    name: RouteName.Report,
    params: {
      reportId: report.id,
    },
  });
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Order {{ serviceOrder?.id }}</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else-if="serviceOrder" class="columns">
      <div class="column is-8">
        <div v-for="(field, index) in fields" :key="index" class="field">
          <label class="label">{{ field.title }}</label>
          <div class="control">
            <template v-if="field.key === 'description'">
              <textarea
                readonly
                disabled
                class="textarea"
                :value="field.value()"
                rows="6"
              ></textarea>
            </template>

            <template v-else>
              <input class="input" readonly disabled :value="field.value()" />
            </template>
          </div>
        </div>
      </div>

      <div class="column">
        <section
          class="section notification has-background-aero-light has-text-aero-dark"
        >
          <p class="title is-5">Historik</p>

          <app-table
            :columns="historyColumns"
            :items="serviceOrder.statusChanges"
            :show-search="false"
            :sort-column="{ key: 'time', asc: false }"
          >
            <template #column:time="{ value }">{{
              formatDate(value)
            }}</template>
          </app-table>
        </section>
        <section
          class="section notification has-background-aero-light has-text-aero-dark"
        >
          <p class="title is-5">Noteringar</p>

          <app-table
            :columns="notesColumns"
            :items="serviceOrder.notes"
            :show-search="false"
            :sort-column="{ key: 'time', asc: false }"
          >
            <template #column:time="{ value }">{{
              formatDate(value)
            }}</template>
          </app-table>
        </section>
        <section
          class="section notification has-background-aero-light has-text-aero-dark"
        >
          <p class="title is-5">Rapporter</p>

          <app-table
            :columns="reportsColumns"
            :items="serviceOrder.reports"
            :show-search="false"
            :sort-column="{ key: 'time', asc: false }"
            @click:row="onClickReport"
          >
            <template #column:time="{ value }">{{
              formatDate(value)
            }}</template>
          </app-table>
        </section>

        <section class="section notification is-light">
          <div class="columns is-variable is-justify-space-between">
            <div class="column is-flex is-align-items-center">
              <p class="title is-5">Filer</p>
            </div>

            <div class="column is-narrow">
              <label class="file-label" for="fileDownload">
                <button
                  type="button"
                  class="button is-normal"
                  :class="{ 'is-loading': fileLoading }"
                  @click="onClickDownloadFiles"
                >
                  <span class="file-icon">
                    <app-icon :icon="mdiFolderDownload" />
                  </span>
                  <span class="file-label">Hämta</span>
                </button>
              </label>
            </div>
          </div>

          <div class="columns is-multiline">
            <div class="column is-12">
              <app-table :columns="filesColumns" :items="serviceOrder.files">
                <template #column:name="{ value, item }">
                  <a @click="onClickDownloadFile(item)">
                    {{ value }}
                  </a>
                </template>
                <template #column:download="{ item }">
                  <label class="checkbox" for="selectForDownload">
                    <input
                      id="selectForDownload"
                      type="checkbox"
                      @change="onSelectedForDownload(item)"
                    />
                  </label>
                </template>
              </app-table>
            </div>

            <div class="column is-12">
              <form @submit.prevent="onSubmitUpload">
                <div class="field">
                  <div class="file has-name">
                    <label class="file-label" for="errorReportFile">
                      <input
                        id="errorReportFile"
                        class="file-input"
                        type="file"
                        name="errorReportFile"
                        @change="onUploadFile"
                      />
                      <span class="file-cta">
                        <span class="file-icon">
                          <app-icon :icon="mdiUpload" />
                        </span>
                        <span class="file-label">Välj en fil</span>
                      </span>
                      <span v-if="file" class="file-name">{{ file.name }}</span>
                    </label>
                  </div>
                </div>

                <div class="field">
                  <img
                    v-if="base64ImageData"
                    :src="base64ImageData"
                    alt="Vald bild"
                    class="preview-image"
                  />
                </div>

                <button
                  type="submit"
                  class="button is-primary"
                  :class="{ 'is-loading': fileLoading }"
                >
                  Ladda upp
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>
